/* PartnerEcosystemPage.jsx — Full Ecosystem View.
 *
 * The dedicated multi-party operational coordination surface. Houses the
 * full PartnerEcosystemPanel plus benchmarking leaderboards per partner
 * type and a calm executive ecosystem narrative.
 */
import React, { useEffect, useState } from "react";
import {
  Network, Trophy, Gauge, Users,
} from "lucide-react";
import { api } from "../lib/api";
import { PartnerEcosystemPanel } from "../components/mission-control/PartnerEcosystemPanel";

const BAND_TONE = {
  Strong:    { fg: "#047857", dot: "#10B981" },
  Stable:    { fg: "#4338CA", dot: "#818CF8" },
  Watching:  { fg: "#92400E", dot: "#F59E0B" },
  Developing:{ fg: "#9F1239", dot: "#F43F5E" },
};

/* ─── Hero ─────────────────────────────────────────────────────── */
const Hero = ({ summary, narrative }) => (
  <header
    className="relative overflow-hidden rounded-3xl mb-7 px-6 sm:px-9 lg:px-10 py-7 sm:py-9"
    data-testid="partners-page-hero"
    style={{
      background: "linear-gradient(135deg, #060812 0%, #161937 45%, #2A1F5A 100%)",
      boxShadow: "0 30px 80px -36px rgba(15,23,42,0.55), inset 0 0 0 1px rgba(255,255,255,0.05)",
    }}
  >
    <span aria-hidden className="absolute -top-16 -left-12 w-[360px] h-[360px] rounded-full blur-3xl pointer-events-none"
      style={{ background: "radial-gradient(circle, rgba(139,92,246,0.40), transparent 65%)" }} />
    <span aria-hidden className="absolute -bottom-20 right-12 w-[420px] h-[420px] rounded-full blur-3xl pointer-events-none"
      style={{ background: "radial-gradient(circle, rgba(56,189,248,0.18), transparent 70%)" }} />
    <div className="relative">
      <div className="inline-flex items-center gap-2 mb-3">
        <Network size={13} className="text-violet-300" strokeWidth={2.2} />
        <p className="text-[10.5px] uppercase tracking-[0.22em] font-bold text-violet-200/90">
          Partner Ecosystem
        </p>
      </div>
      <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-[1.05] mb-3"
          data-testid="partners-page-title">
        Ecosystem Operations
      </h1>
      <p className="text-[14px] sm:text-[15px] text-white/65 leading-relaxed max-w-2xl">
        Multi-party operational coordination across solicitors, brokers, surveyors
        and the wider transaction ecosystem.
      </p>
      {narrative ? (
        <p className="mt-4 text-[12.5px] text-white/55 leading-relaxed max-w-2xl">
          {narrative}
        </p>
      ) : null}
    </div>
  </header>
);

/* ─── Benchmark leaderboards ───────────────────────────────────── */
const Leaderboards = ({ leaderboards }) => {
  if (!leaderboards?.length) return null;
  return (
    <section className="mb-7" data-testid="partners-leaderboards">
      <div className="flex items-center gap-2.5 mb-3.5">
        <div className="w-7 h-7 rounded-md inline-flex items-center justify-center"
          style={{ background: "rgba(245,158,11,0.12)", color: "#92400E" }}>
          <Trophy size={13} strokeWidth={2.3} />
        </div>
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-[0.16em] font-bold text-amber-700">
            Partner Benchmarking
          </p>
          <h2 className="text-[13.5px] font-semibold text-slate-900 truncate">
            Top performers per partner type
          </h2>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {leaderboards.map((lb) => (
          <div key={lb.partner_type} className="gp-card p-4"
               data-testid={`partners-lb-${lb.partner_type}`}>
            <p className="text-[10px] uppercase tracking-[0.16em] font-bold text-violet-700 mb-3">
              {lb.type_label}
            </p>
            <div className="space-y-2.5">
              {(lb.leaders || []).slice(0, 5).map((p, i) => {
                const t = BAND_TONE[p.band] || BAND_TONE.Stable;
                return (
                  <div key={p.partner_key} className="flex items-center gap-2.5"
                       data-testid={`partners-lb-row-${p.partner_key}`}>
                    <span className="text-[11px] font-bold tabular-nums text-slate-400 w-4">
                      {i + 1}
                    </span>
                    <span aria-hidden className="w-1.5 h-1.5 rounded-full shrink-0"
                      style={{ background: t.dot }} />
                    <p className="text-[11.5px] font-semibold text-slate-700 flex-1 truncate">
                      {p.name}
                    </p>
                    <p className="text-[11px] font-semibold tabular-nums shrink-0"
                       style={{ color: t.fg }}>
                      {p.composite_score}
                    </p>
                  </div>
                );
              })}
              {(!lb.leaders || lb.leaders.length === 0) ? (
                <p className="text-[11px] text-slate-400 italic">No partners yet.</p>
              ) : null}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

/* ═══════════════════════════════════════════════════════════════════
   Page
   ─────────────────────────────────────────────────────────────────── */
export default function PartnerEcosystemPage() {
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    (async () => {
      try {
        const { data: resp } = await api.get("/api/partners/ecosystem/overview");
        if (alive) setData(resp?.ecosystem || null);
      } catch (err) {
        if (alive) setError(err?.response?.status === 403
          ? "Partner Ecosystem is available to admin / director / manager roles only."
          : "Could not load Partner Ecosystem.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  return (
    <div className="md:ml-[var(--cf-sidebar-w,300px)] transition-[margin-left] duration-300 ease-out min-h-screen px-5 sm:px-7 lg:px-9 pt-6 pb-12 overflow-x-hidden"
         data-testid="partners-page">
      {error ? (
        <div className="gp-card p-6 text-center max-w-xl mx-auto"
             data-testid="partners-error">
          <Network size={20} className="text-violet-500 mx-auto mb-3" />
          <p className="text-[13.5px] font-semibold text-slate-900 mb-1">{error}</p>
        </div>
      ) : loading ? (
        <div className="gp-card p-6 cf-pulse">
          <div className="h-3 w-40 bg-slate-100 rounded mb-3" />
          <div className="h-4 w-72 bg-slate-100 rounded mb-2" />
        </div>
      ) : data ? (
        <>
          <Hero summary={data.summary} narrative={data.narrative} />
          <Leaderboards leaderboards={data.benchmarks?.leaderboards || []} />
          <PartnerEcosystemPanel />
        </>
      ) : null}
    </div>
  );
}
